// src/ui/Field.tsx (T1): form parts in Direction D. Field wraps one control with its label, a one-line hint and
// the error (spec 4.1 `fields`). Option rows (.opt) and option cards (.rolecard, the only 12px corners) are
// radio groups with arrow-key movement. Root classes: hb-field, hb-input, hb-textarea, hb-select, hb-options,
// hb-option-cards, hb-switch.
import type { ComponentChildren, InputHTMLAttributes, SelectHTMLAttributes, TextareaHTMLAttributes } from "preact";
import { useId } from "preact/hooks";
import { cx } from "./cx";
import { Icon, type IconName } from "./Icon";

export function Field(props: {
  label: ComponentChildren;
  hint?: ComponentChildren;
  error?: string | null;
  /** Renders the control; spread the given props onto it so the label and messages are wired up. */
  children: (control: { id: string; "aria-describedby"?: string; "aria-invalid"?: "true" }) => ComponentChildren;
  class?: string;
}) {
  const id = useId();
  const hintId = props.hint ? `${id}-hint` : undefined;
  const errId = props.error ? `${id}-err` : undefined;
  const describedBy = [errId, hintId].filter(Boolean).join(" ") || undefined;
  return (
    <div class={cx("hb-field", "field", props.error && "is-invalid", props.class)}>
      <label for={id}>{props.label}</label>
      {props.children({ id, "aria-describedby": describedBy, ...(props.error ? { "aria-invalid": "true" as const } : {}) })}
      {props.error ? (
        <p class="hb-field-error" id={errId}>
          <Icon name="info" size="sm" />
          {props.error}
        </p>
      ) : null}
      {props.hint ? (
        <p class="hint" id={hintId}>
          {props.hint}
        </p>
      ) : null}
    </div>
  );
}

type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "class" | "className"> & { class?: string };
export function TextInput(props: InputProps) {
  const { class: extra, ...rest } = props;
  return <input type="text" {...rest} class={cx("hb-input", "input", extra)} />;
}

type TextAreaProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "class" | "className"> & { class?: string };
export function TextArea(props: TextAreaProps) {
  const { class: extra, ...rest } = props;
  return <textarea {...rest} class={cx("hb-textarea", "textarea", extra)} />;
}

type SelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, "class" | "className"> & {
  class?: string;
  options: readonly { value: string; label: string; disabled?: boolean }[];
};
export function Select(props: SelectProps) {
  const { class: extra, options, ...rest } = props;
  return (
    <select {...rest} class={cx("hb-select", "select", extra)}>
      {options.map((o) => (
        <option key={o.value} value={o.value} disabled={o.disabled}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function radioKeys<V extends string>(options: readonly { value: V }[], value: V | null, onChange: (v: V) => void) {
  return (e: KeyboardEvent) => {
    const keys = ["ArrowDown", "ArrowRight", "ArrowUp", "ArrowLeft"];
    if (!keys.includes(e.key)) return;
    e.preventDefault();
    const i = Math.max(0, options.findIndex((o) => o.value === value));
    const step = e.key === "ArrowDown" || e.key === "ArrowRight" ? 1 : -1;
    const next = options[(i + step + options.length) % options.length];
    if (!next) return;
    onChange(next.value);
    const group = e.currentTarget as HTMLElement;
    requestAnimationFrame(() => group.querySelector<HTMLElement>(`[data-value="${CSS.escape(next.value)}"]`)?.focus());
  };
}

/** Choose-one rows with a navy edge, tint and a filled check (D's .opt, from Sort). */
export function OptionRows<V extends string>(props: {
  label: string;
  options: readonly { value: V; label: ComponentChildren }[];
  value: V | null;
  onChange: (v: V) => void;
  class?: string;
}) {
  const { options, value, onChange } = props;
  const focusable = value ?? options[0]?.value;
  return (
    <div class={cx("hb-options", props.class)} role="radiogroup" aria-label={props.label} onKeyDown={radioKeys(options, value, onChange)}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          class="opt"
          data-value={o.value}
          aria-checked={o.value === value ? "true" : "false"}
          tabIndex={o.value === focusable ? 0 : -1}
          onClick={() => onChange(o.value)}
        >
          <span class="opt-check" aria-hidden="true">
            <Icon name="check" />
          </span>
          <span class="opt-name">{o.label}</span>
        </button>
      ))}
    </div>
  );
}

/** Option cards (.rolecard: an icon, a name, one short line). */
export function OptionCards<V extends string>(props: {
  label: string;
  options: readonly { value: V; label: ComponentChildren; sub?: ComponentChildren; icon?: IconName }[];
  value: V | null;
  onChange: (v: V) => void;
  columns?: 2 | 3;
  class?: string;
}) {
  const { options, value, onChange } = props;
  const focusable = value ?? options[0]?.value;
  return (
    <div
      class={cx("hb-option-cards", "roles", props.columns === 3 && "roles-3", props.class)}
      role="radiogroup"
      aria-label={props.label}
      onKeyDown={radioKeys(options, value, onChange)}
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          class="rolecard"
          data-value={o.value}
          aria-checked={o.value === value ? "true" : "false"}
          tabIndex={o.value === focusable ? 0 : -1}
          onClick={() => onChange(o.value)}
        >
          {o.icon ? <Icon name={o.icon} /> : null}
          <b>{o.label}</b>
          {o.sub ? <span>{o.sub}</span> : null}
        </button>
      ))}
    </div>
  );
}

/** An on/off switch (D's .switch: an outlined track when off, navy when on). Needs a visible label nearby. */
export function Switch(props: { checked: boolean; onChange: (checked: boolean) => void; label: string; disabled?: boolean; class?: string }) {
  return (
    <button
      type="button"
      role="switch"
      class={cx("hb-switch", "switch", props.class)}
      aria-checked={props.checked ? "true" : "false"}
      aria-label={props.label}
      disabled={props.disabled}
      onClick={() => props.onChange(!props.checked)}
    />
  );
}
